// ── recurrence.js ─────────────────────────────────────────────────────────
// Pure functions — no imports needed. Used by useTasks (offline toggle)
// and RecurrencePicker (UI). Mirrors the PHP RecurrenceHelper exactly.

export const RECURRENCE_TYPES = [
  { value: 'none',    label: 'Does not repeat' },
  { value: 'daily',   label: 'Daily'           },
  { value: 'weekly',  label: 'Weekly'          },
  { value: 'monthly', label: 'Monthly'         },
  { value: 'custom',  label: 'Custom interval' },
];

export const WEEKDAYS = [
  { value: 0, label: 'Su' },
  { value: 1, label: 'Mo' },
  { value: 2, label: 'Tu' },
  { value: 3, label: 'We' },
  { value: 4, label: 'Th' },
  { value: 5, label: 'Fr' },
  { value: 6, label: 'Sa' },
];

/**
 * Compute next due date after `fromDate` for the given recurrence.
 * @param {string}      type   'daily'|'weekly'|'monthly'|'custom'
 * @param {object|null} config parsed config object
 * @param {Date}        fromDate
 * @returns {Date|null}
 */
export function computeNextDue(type, config, fromDate) {
  const from = new Date(fromDate);
  from.setHours(0, 0, 0, 0);

  switch (type) {
    case 'daily': {
      const d = new Date(from);
      d.setDate(d.getDate() + 1);
      return d;
    }

    case 'weekly': {
      const days = (config?.days ?? [1]).slice().sort((a, b) => a - b);
      const cursor = new Date(from);
      cursor.setDate(cursor.getDate() + 1); // start searching tomorrow
      for (let i = 0; i < 8; i++) {
        if (days.includes(cursor.getDay())) return cursor;
        cursor.setDate(cursor.getDate() + 1);
      }
      // Fallback — should never hit with valid config
      const fallback = new Date(from);
      fallback.setDate(fallback.getDate() + 7);
      return fallback;
    }

    case 'monthly': {
      const day = Math.max(1, Math.min(28, config?.day ?? 1));
      const next = new Date(from);
      next.setMonth(next.getMonth() + 1);
      next.setDate(day);
      return next;
    }

    case 'custom': {
      const interval = Math.max(1, config?.interval ?? 1);
      const d = new Date(from);
      d.setDate(d.getDate() + interval);
      return d;
    }

    default:
      return null;
  }
}

/**
 * Format next due date as a human-readable string.
 * e.g. "Tomorrow", "Mon, Mar 17", "Mar 17"
 */
export function formatNextDue(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T00:00:00');
  const today = new Date(); today.setHours(0,0,0,0);
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  const diff = Math.round((d - today) / 86400000);

  if (diff === 0)  return 'Today';
  if (diff === 1)  return 'Tomorrow';
  if (diff <= 6)   return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Human-readable description of a recurrence rule.
 * e.g. "Every day", "Every Mon, Wed, Fri", "Every month on the 15th"
 */
export function describeRecurrence(type, config) {
  if (!type || type === 'none') return null;

  switch (type) {
    case 'daily':
      return 'Every day';

    case 'weekly': {
      const days = (config?.days ?? [1]).slice().sort((a, b) => a - b);
      const names = WEEKDAYS.filter(d => days.includes(d.value)).map(d => d.label);
      if (days.length === 7) return 'Every day';
      if (days.length === 5 && !days.includes(0) && !days.includes(6)) return 'Weekdays';
      if (days.length === 2 && days.includes(0) && days.includes(6)) return 'Weekends';
      return `Every ${names.join(', ')}`;
    }

    case 'monthly': {
      const day = config?.day ?? 1;
      const suffix = day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th';
      return `Monthly on the ${day}${suffix}`;
    }

    case 'custom': {
      const n = config?.interval ?? 1;
      return `Every ${n} day${n === 1 ? '' : 's'}`;
    }

    default:
      return null;
  }
}

/**
 * Given a task object, advance its recurrence offline.
 * Mirrors Task::advanceRecurrence() in PHP.
 * Returns updated task fields (does NOT mutate the original).
 */
export function advanceRecurrenceOffline(task) {
  if (!task.recurrence_type || task.recurrence_type === 'none') {
    // Non-recurring: just toggle done
    return { ...task, done: !task.done, completed_at: !task.done ? new Date().toISOString() : null };
  }

  // Recurring: reset done and advance next_due_date
  const base = task.next_due_date
    ? new Date(task.next_due_date + 'T00:00:00')
    : new Date();

  const config = typeof task.recurrence_config === 'string'
    ? JSON.parse(task.recurrence_config)
    : task.recurrence_config;

  const nextDate = computeNextDue(task.recurrence_type, config, base);
  const nextStr  = nextDate ? nextDate.toISOString().split('T')[0] : null;

  return {
    ...task,
    done         : false,
    completed_at : null,
    next_due_date: nextStr,
    due_date     : nextStr,
    // Reset subtasks for next cycle
    subtasks: (task.subtasks || []).map(s => ({ ...s, done: false, completed_at: null })),
  };
}

/**
 * Build default config for a recurrence type.
 */
export function defaultConfig(type) {
  switch (type) {
    case 'weekly':  return { days: [1] };          // Monday
    case 'monthly': return { day: new Date().getDate() }; // current day of month
    case 'custom':  return { interval: 7 };         // every 7 days
    default:        return null;
  }
}